import React, { useRef, useEffect } from 'react'
import { Input, Space } from 'antd'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faSearch } from '@fortawesome/free-solid-svg-icons'
import Button from '../../Button.jsx'
import type { FilterConfig, FilterLocalState, FilterSelectedKey } from '../TableViewTypes'
import { textValueFromSelectedKey } from './filterSelectedKey.ts'

interface TextFilterDropdownProps {
  columnKey: string
  columnTitle?: string
  actualFilterKey: string
  filterLocalState: FilterLocalState
  setFilterLocalState: React.Dispatch<React.SetStateAction<FilterLocalState>>
  syncedFilterKeys: React.MutableRefObject<Set<string>>
  selectedKeys: FilterSelectedKey[]
  setSelectedKeys: (selectedKeys: FilterSelectedKey[]) => void
  confirm: () => void
  onFiltersChange?: (filters: FilterConfig) => void
}

export default function TextFilterDropdown({
  columnKey,
  columnTitle,
  actualFilterKey,
  filterLocalState,
  setFilterLocalState,
  syncedFilterKeys,
  selectedKeys,
  setSelectedKeys,
  confirm,
  onFiltersChange
}: TextFilterDropdownProps) {
  const localValue = filterLocalState[columnKey]?.text
  const currentValue = localValue !== undefined ? localValue : textValueFromSelectedKey(selectedKeys[0])
  const hasValue = currentValue && currentValue.length > 0

  const syncKey = `${columnKey}-${localValue || 'empty'}`
  if (localValue !== undefined && !syncedFilterKeys.current.has(syncKey)) {
    const normalizedLocalValue = localValue || ''
    const normalizedSelectedKey = selectedKeys[0] || ''
    if (normalizedLocalValue !== normalizedSelectedKey) {
      syncedFilterKeys.current.add(syncKey)
      requestAnimationFrame(() => {
        setSelectedKeys(normalizedLocalValue ? [normalizedLocalValue] : [])
        setTimeout(() => {
          syncedFilterKeys.current.delete(syncKey)
        }, 100)
      })
    }
  }

  const handleApply = () => {
    if (hasValue) {
      const filterValue = currentValue ? [currentValue] : []
      setSelectedKeys(filterValue)

      const filterConfig: FilterConfig = {}
      filterConfig[actualFilterKey] = currentValue
      onFiltersChange?.(filterConfig)

      setFilterLocalState((prev) => {
        const newState = { ...prev }
        delete newState[columnKey]
        return newState
      })

      confirm()
    }
  }

  const handleClear = () => {
    setFilterLocalState((prev) => {
      const newState = { ...prev }
      delete newState[columnKey]
      return newState
    })

    setSelectedKeys([])

    const filterConfig: FilterConfig = {}
    filterConfig[actualFilterKey] = undefined
    onFiltersChange?.(filterConfig)

    confirm()
  }

  return (
    <div style={{ padding: 8 }}>
      <Input
        placeholder={`Search ${columnTitle}`}
        value={currentValue}
        onChange={(e) => {
          const newValue = e.target.value
          setFilterLocalState((prev) => ({
            ...prev,
            [columnKey]: { ...prev[columnKey], text: newValue }
          }))
          setSelectedKeys(newValue ? [newValue] : [])
        }}
        autoFocus
        ref={(input) => {
          if (input) {
            setTimeout(() => {
              try {
                if ((input as any).input && typeof (input as any).input.focus === 'function') {
                  ;(input as any).input.focus()
                } else if (input.focus && typeof input.focus === 'function') {
                  input.focus()
                } else if ((input as any).querySelector) {
                  const actualInput = (input as any).querySelector('input')
                  if (actualInput) actualInput.focus()
                }
              } catch (e) {}
            }, 150)
          }
        }}
        onPressEnter={handleApply}
        style={{
          marginBottom: 8,
          display: 'block',
          width: 300
        }}
      />
      <Space>
        <Button variant='primary' size='small' onClick={handleApply} disabled={!hasValue}>
          Search
        </Button>
        <Button variant='cancel' size='small' onClick={handleClear}>
          Clear
        </Button>
      </Space>
    </div>
  )
}

export function TextFilterIcon(filtered: boolean) {
  return <FontAwesomeIcon icon={faSearch} style={{ color: filtered ? '#1890ff' : '#9CA3AF', padding: '0 4px' }} />
}
